import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  PpmpItemCatalogEntry,
  PpmpItemCatalogOption,
  PpmpItemCatalogRequest,
  ProcurementCatalogEntry,
  ProcurementCatalogOption,
  ProcurementCatalogRequest,
  ProcurementCatalogSlug,
} from './procurementCatalogTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Sent whenever the key is present, empty string included — an empty value is the reader
    saying "none of these", which matches no row. A truthiness check would drop it and the
    server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

const base = (catalog: ProcurementCatalogSlug) => `/api/procurement-catalogs/${catalog}`;

/**
 * The two flat procurement vocabularies, addressed by slug.
 *
 * **One client over two resources**, matching the one controller behind them: the catalog is a
 * route segment, so every call takes it first. That is what lets one index page and one profile
 * page serve both.
 */
export const procurementCatalogsApi = {
  list: (catalog: ProcurementCatalogSlug, query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<ProcurementCatalogEntry>>(`${base(catalog)}${toQueryString(query)}`, { signal }),

  get: (catalog: ProcurementCatalogSlug, id: string, signal?: AbortSignal) =>
    api<ProcurementCatalogEntry>(`${base(catalog)}/${id}`, { signal }),

  /**
   * The ACTIVE rows, alphabetically.
   *
   * What a PPMP line's pickers are built from. Deleted rows are left out — a picker asks what
   * can be chosen now; a line already filed under a retired row keeps its snapshotted word.
   */
  options: (catalog: ProcurementCatalogSlug, signal?: AbortSignal) =>
    api<ProcurementCatalogOption[]>(`${base(catalog)}/options`, { signal }),

  /**
   * Adds one row.
   *
   * **Idempotent on the name**, because both are added in place from a PPMP line's own pickers:
   * a planner who types one that already exists wanted that row rather than an error about it.
   * The cost, stated rather than hidden — this catalog's own create modal no longer reports a
   * duplicate name, it returns the row holding it. An UPDATE still refuses one.
   */
  create: (catalog: ProcurementCatalogSlug, body: ProcurementCatalogRequest) =>
    api<ProcurementCatalogEntry>(base(catalog), { method: 'POST', body }),

  update: (catalog: ProcurementCatalogSlug, id: string, body: ProcurementCatalogRequest) =>
    api<ProcurementCatalogEntry>(`${base(catalog)}/${id}`, { method: 'PUT', body }),

  /** Soft delete. PPMP lines filed under it keep the snapshotted name. */
  remove: (catalog: ProcurementCatalogSlug, id: string) =>
    api<void>(`${base(catalog)}/${id}`, { method: 'DELETE' }),

  restore: (catalog: ProcurementCatalogSlug, id: string) =>
    api<void>(`${base(catalog)}/${id}/restore`, { method: 'POST' }),

  /** Soft-deletes every row of ONE vocabulary; resolves with how many. */
  clear: (catalog: ProcurementCatalogSlug) =>
    api<number>(`${base(catalog)}/clear`, { method: 'POST' }),
};

/**
 * The PPMP item catalog — its own module, because its row is a different shape.
 */
export const ppmpItemsApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<PpmpItemCatalogEntry>>(`/api/ppmp-items${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<PpmpItemCatalogEntry>(`/api/ppmp-items/${id}`, { signal }),

  /** The ACTIVE entries, whole — a pick snapshots three fields onto the line. */
  options: (signal?: AbortSignal) =>
    api<PpmpItemCatalogOption[]>('/api/ppmp-items/options', { signal }),

  /** Adds one entry. Idempotent on the description's PLAIN TEXT, never its markup. */
  create: (body: PpmpItemCatalogRequest) =>
    api<PpmpItemCatalogEntry>('/api/ppmp-items', { method: 'POST', body }),

  update: (id: string, body: PpmpItemCatalogRequest) =>
    api<PpmpItemCatalogEntry>(`/api/ppmp-items/${id}`, { method: 'PUT', body }),

  remove: (id: string) => api<void>(`/api/ppmp-items/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`/api/ppmp-items/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>('/api/ppmp-items/clear', { method: 'POST' }),
};
